// «Файл»: сохранение и версии (автоснимки, именованные, «Сдано преподавателю», сравнение и восстановление),
// экспорт DOCX / PDF / Markdown / архив, «Открыть в Word», печать через PDF, переименование и копия отчёта.
import { backHash } from '../auth/session';
import { useEffect, useMemo, useState } from 'react';
import type { RegistryApi, Ctx, DialogProps } from '../../core/registry';
import type { JSONContent } from '@tiptap/core';
import { saveNow, closeProject } from '../../core/project';
import { appStore } from '../../core/store';
import type { BuildResult } from '../../core/types';
import { uiStore } from '../../ui/uiState';
import { askConfirm, askText } from '../../ui/prompts';
import { Btn, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { shellApi } from './shellApi';
import { diffDocs, diffStats, type Row } from './diff';

/** Скачать файл результата (браузер сохранит его в «Загрузки»). */
export function download(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const NAMES = { docx: 'DOCX', pdf: 'PDF', md: 'Markdown', zip: 'архив проекта' };

/** Собрать файл нужного формата (DOCX/PDF - через writer и Word). Возвращает имя файла в export/. */
export async function buildFile(ctx: Ctx, format: 'docx' | 'pdf' | 'md' | 'zip'): Promise<string | undefined> {
  const pid = ctx.store.getState().projectId;
  if (!pid) return undefined;
  if (uiStore.getState().task) { ctx.toast('Подождите: идёт ' + uiStore.getState().task!.label.toLowerCase(), 'info'); return undefined; }
  await saveNow();
  const rev = ctx.store.getState().revision;
  uiStore.getState().set({ task: { label: `Экспорт в ${NAMES[format]}`, short: NAMES[format], progress: 'Запуск…' } });
  try {
    const r = await ctx.api.exportFile(pid, format);
    let file = r.file;
    if (r.job) {
      const res = await ctx.api.waitJob<BuildResult>(r.job, (s) => uiStore.getState().set({ task: { label: `Экспорт в ${NAMES[format]}`, short: NAMES[format], progress: s.progress } }));
      file = format === 'pdf' ? res.pdf || undefined : res.docx;
      if (res.pdf) uiStore.getState().set({ built: { pages: res.pages, revision: rev, at: Date.now(), pdf: res.pdf } });
      if (!file) throw new Error((res as any).pdf_error || 'PDF не создан: для PDF нужен Microsoft Word');
    }
    return file;
  } finally {
    uiStore.getState().set({ task: null });
  }
}

export async function exportAs(ctx: Ctx, format: 'docx' | 'pdf' | 'md' | 'zip') {
  const file = await buildFile(ctx, format);
  if (!file) return;
  download(ctx.api.fileUrl(ctx.store.getState().projectId!, file));
  // файл выгружен, но живая проверка видит ошибки СТО - напоминаем (сводка - «СТО → Готовность»)
  const errs = (format === 'docx' || format === 'pdf') ? ctx.store.getState().lint.filter((l) => l.sev === 'E').length : 0;
  if (errs) ctx.toast(`Готово: ${file}. В тексте ошибок СТО: ${errs} - см. панель «Проверка» или «СТО → Готовность»`, 'warn', 8000);
  else ctx.toast(`Готово: ${file} - в папке «Загрузки»`, 'ok', 5000);
}

/** Самый свежий файл нужного вида, если он новее последнего сохранения документа. */
async function freshFile(ctx: Ctx, kind: 'docx' | 'pdf'): Promise<string | undefined> {
  const pid = ctx.store.getState().projectId!;
  const at = Date.parse(ctx.store.getState().save.at || '') / 1000 || 0;
  const { files } = await shellApi.exports(pid);
  const f = files.find((x) => x.kind === kind);
  return f && ctx.store.getState().save.state === 'saved' && f.mtime >= at ? f.name : undefined;
}

async function openInWord(ctx: Ctx) {
  const pid = ctx.store.getState().projectId!;
  await saveNow();
  const file = (await freshFile(ctx, 'docx')) || (await buildFile(ctx, 'docx'));
  if (!file) return;
  await shellApi.openWord(pid, file);
  ctx.toast(`Открываю ${file} в Word…`, 'ok');
}

async function printDoc(ctx: Ctx) {
  const pid = ctx.store.getState().projectId!;
  await saveNow();
  const file = (await freshFile(ctx, 'pdf')) || (await buildFile(ctx, 'pdf'));
  if (!file) return;
  const w = window.open(ctx.api.fileUrl(pid, file, true), '_blank');
  ctx.toast(w ? 'PDF открыт в новой вкладке: печать - Ctrl+P' : 'Разрешите всплывающие окна или скачайте PDF', w ? 'ok' : 'warn', 6000);
}

export async function snapshot(ctx: Ctx, label?: string) {
  const pid = ctx.store.getState().projectId!;
  let l = label;
  if (l === undefined) {
    l = await askText({ title: 'Сохранить версию', label: 'Подпись версии', value: '', placeholder: 'например: «Перед правками руководителя»',
      presets: ['Черновик', 'Перед правками руководителя', 'Сдано преподавателю', 'После замечаний'], allowEmpty: true, ok: 'Сохранить версию',
      testid: 'snapshot-dialog' });
    if (l === undefined) return;
  }
  await saveNow(true);
  await ctx.api.snapshot(pid, l);
  ctx.toast(l ? `Версия «${l}» сохранена` : 'Версия сохранена', 'ok');
}

async function rename(ctx: Ctx) {
  const pid = ctx.store.getState().projectId!;
  const n = await askText({ title: 'Переименовать отчёт', label: 'Имя отчёта', value: pid.replace(/^u\d+:/, ''), ok: 'Переименовать', testid: 'rename-dialog' });
  if (!n || n.trim() === pid.replace(/^u\d+:/, '')) return;
  await saveNow();
  const res = await ctx.api.renameProject(pid, n.trim());
  location.hash = '#/p/' + encodeURIComponent(res.id);
  ctx.toast('Отчёт переименован', 'ok');
}

async function duplicate(ctx: Ctx) {
  const pid = ctx.store.getState().projectId!;
  const n = await askText({ title: 'Копия отчёта', label: 'Имя копии', value: pid.replace(/^u\d+:/, '') + ' - копия', ok: 'Создать копию' });
  if (!n) return;
  await saveNow();
  const res = await ctx.api.duplicateProject(pid, n.trim());
  const go = await askConfirm({ title: 'Копия создана', text: `«${res.id}» - открыть её сейчас?`, ok: 'Открыть копию', cancel: 'Остаться здесь' });
  if (go) location.hash = '#/p/' + encodeURIComponent(res.id);
}

export function setupFile(r: RegistryApi) {
  r.commands.add({ id: 'file.save', title: 'Сохранить', description: 'Документ сохраняется сам через секунду после правки; Ctrl+S - сразу',
    icon: 'save', shortcut: 'Mod-s', run: async (c) => { await saveNow(true); if (c.store.getState().save.state === 'saved') c.toast('Сохранено', 'ok', 1500); } });
  r.commands.add({ id: 'file.saveForce', title: 'Сохранить поверх', description: 'При конфликте версий записать этот документ поверх изменений из другой вкладки',
    icon: 'save', run: () => saveNow(true, true) });
  r.commands.add({ id: 'file.snapshot', title: 'Сохранить версию…', description: 'Именованный снимок: к нему можно вернуться и сравнить',
    icon: 'bookmark', shortcut: 'Mod-Alt-s', run: (c) => snapshot(c) });
  r.commands.add({ id: 'file.submitted', title: 'Отметить «Сдано преподавателю»', description: 'Версия с датой сдачи - потом видно, что изменилось после',
    icon: 'badge', run: (c) => snapshot(c, 'Сдано преподавателю ' + new Date().toLocaleDateString('ru-RU')) });
  r.commands.add({ id: 'file.versions', title: 'Версии и сравнение', description: 'Снимки документа: сравнить с текущим, восстановить', icon: 'history',
    run: (c) => c.openDialog('shell.versions') });
  r.commands.add({ id: 'file.exportDocx', title: 'Экспорт в DOCX', description: 'Собрать отчёт по СТО в Word (.docx) и скачать', icon: 'docx', run: (c) => exportAs(c, 'docx') });
  r.commands.add({ id: 'file.exportPdf', title: 'Экспорт в PDF', description: 'PDF с закладками (через Word)', icon: 'pdf', run: (c) => exportAs(c, 'pdf') });
  r.commands.add({ id: 'file.exportMd', title: 'Экспорт в Markdown', description: 'report.md для «sto build» + рисунки', icon: 'file', run: (c) => exportAs(c, 'md') });
  r.commands.add({ id: 'file.exportZip', title: 'Архив проекта (.zip)', description: 'Резервная копия или перенос на другой компьютер', icon: 'zip', run: (c) => exportAs(c, 'zip') });
  r.commands.add({ id: 'file.openWord', title: 'Открыть в Word', description: 'Собрать DOCX и открыть его в Microsoft Word', icon: 'external', run: (c) => openInWord(c) });
  r.commands.add({ id: 'file.print', title: 'Печать', description: 'Собрать PDF и открыть его для печати', icon: 'print', shortcut: 'Mod-p', run: (c) => printDoc(c) });
  r.commands.add({ id: 'file.reveal', title: 'Открыть папку отчёта', description: 'Папка с готовыми DOCX и PDF в Проводнике', icon: 'open',
    run: (c) => c.api.reveal(c.store.getState().projectId!, 'export') });
  r.commands.add({ id: 'file.close', title: 'К списку отчётов', icon: 'back', run: () => { const to = backHash(); return closeProject().then(() => { location.hash = to; }); } });
  r.commands.add({ id: 'file.backstage', title: 'Меню «Файл»', icon: 'file', run: () => uiStore.getState().set({ backstage: 'info' }) });
  r.commands.add({ id: 'project.rename', title: 'Переименовать отчёт', icon: 'rename', run: (c) => rename(c) });
  r.commands.add({ id: 'project.duplicate', title: 'Создать копию отчёта', icon: 'copy', run: (c) => duplicate(c) });
  r.dialogs.add({ id: 'shell.versions', component: VersionsDialog });
}

// ------------------------------------------------------------------------------------------------ версии
interface Version { id: string; kind: string; label: string; time: string }

function fmt(s: string) {
  const d = new Date(s);
  return isNaN(+d) ? s : d.toLocaleString('ru-RU', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
}

export function VersionsDialog({ ctx, close, props }: DialogProps) {
  const pid = ctx.store.getState().projectId!;
  const [list, setList] = useState<Version[] | null>(null);
  const [sel, setSel] = useState<string | null>(props?.select || null);
  const [ver, setVer] = useState<JSONContent | null>(null);
  const [onlyChanges, setOnlyChanges] = useState(true);
  const load = () => ctx.api.versions(pid).then((r) => setList(r.versions));
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    setVer(null);
    if (sel) ctx.api.loadVersion(pid, sel).then((r) => setVer(r.doc)).catch((e) => ctx.toast(e.message, 'error'));
  }, [sel]);
  const rows: Row[] | null = useMemo(() => (ver && ctx.editor ? diffDocs(ver, ctx.editor.getJSON()) : null), [ver]);
  const st = rows ? diffStats(rows) : null;
  const cur = list?.find((v) => v.id === sel);

  const restore = async () => {
    if (!sel) return;
    const ok = await askConfirm({ title: 'Восстановить версию?', text: `Документ станет таким, как ${fmt(cur!.time)}. Текущее состояние сохранится отдельной версией - его тоже можно будет вернуть.`, ok: 'Восстановить' });
    if (!ok) return;
    await saveNow(true);
    await ctx.api.restoreVersion(pid, sel);
    close();
    location.reload();
  };
  const toCopy = async () => {
    if (!sel || !ver) return;
    const name = await askText({ title: 'Версия в новый отчёт', label: 'Имя нового отчёта', value: `${pid} (${cur?.label || fmt(cur!.time)})`.slice(0, 80), ok: 'Создать' });
    if (!name) return;
    await saveNow(true);
    const full = await ctx.api.loadVersion(pid, sel);
    const dup = await ctx.api.duplicateProject(pid, name);
    await ctx.api.saveProject(dup.id, { doc: full.doc, meta: full.meta, sources: full.sources, force: true });
    ctx.toast(`Создан отчёт «${dup.id}»`, 'ok');
  };

  return (
    <Modal title="Версии документа" onClose={() => close()} width={1040} testid="versions-dialog" className="versions-modal" footer={<>
      <Btn icon="bookmark" label="Сохранить версию…" onClick={async () => { await snapshot(ctx); void load(); }} testid="version-save" />
      <Btn icon="badge" label="Сдано преподавателю" desc="Версия с датой сдачи" onClick={async () => { await snapshot(ctx, 'Сдано преподавателю ' + new Date().toLocaleDateString('ru-RU')); void load(); }} testid="version-submitted" />
      <span className="topbar-spacer" />
      <Btn label="Закрыть" onClick={() => close()} />
    </>}>
      <div className="versions-layout">
        <div className="versions-list" role="listbox" aria-label="Версии">
          {list === null && <div className="muted pad">Загрузка…</div>}
          {list?.length === 0 && <div className="muted pad">Версий пока нет. Автоснимок делается не чаще раза в 10 минут работы; свою версию можно сохранить кнопкой ниже.</div>}
          {list?.map((v) => {
            const submitted = /сдано/i.test(v.label);
            return (
              <button key={v.id} type="button" role="option" aria-selected={sel === v.id} data-testid="version-row"
                className={'version-item' + (sel === v.id ? ' is-sel' : '')} onClick={() => setSel(v.id)}>
                <Icon name={submitted ? 'badge' : v.kind === 'manual' ? 'bookmark' : 'clock'} size={16} />
                <span className="version-main">
                  <span className="version-label">{v.label || (v.kind === 'auto' ? 'Автоснимок' : 'Версия')}</span>
                  <span className="version-time">{fmt(v.time)}</span>
                </span>
                {submitted && <span className="badge badge-ok">сдано</span>}
              </button>
            );
          })}
        </div>
        <div className="versions-diff">
          {!sel && <div className="versions-empty"><Icon name="compare" size={32} /><div>Выберите версию слева - здесь будет видно, что изменилось с тех пор.</div></div>}
          {sel && !rows && <div className="muted pad"><Icon name="spinner" className="spin" /> Загрузка версии…</div>}
          {sel && rows && (
            <>
              <div className="diff-head">
                <span>С версии «{cur?.label || fmt(cur!.time)}» до текущего текста:</span>
                <span className="diff-stat add">+{st!.add}</span><span className="diff-stat del">−{st!.del}</span><span className="diff-stat mod">~{st!.mod}</span>
                <span className="topbar-spacer" />
                <label className="check small"><input type="checkbox" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} /> только изменения</label>
              </div>
              <div className="diff-body" data-testid="version-diff">
                {!st!.add && !st!.del && !st!.mod && <div className="muted pad">Текст не изменился.</div>}
                <DiffRows rows={rows} only={onlyChanges} />
              </div>
              <div className="diff-actions">
                <Btn icon="restore" label="Восстановить эту версию" primary onClick={restore} testid="version-restore" />
                <Btn icon="duplicate" label="Открыть как новый отчёт" onClick={toCopy} />
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function DiffRows({ rows, only }: { rows: Row[]; only: boolean }) {
  const out: React.ReactNode[] = [];
  let skipped = 0;
  const flush = (k: number) => { if (skipped) { out.push(<div key={'s' + k} className="diff-skip">… без изменений: {skipped}</div>); skipped = 0; } };
  rows.forEach((r, i) => {
    const near = rows[i - 1]?.op !== 'same' || rows[i + 1]?.op !== 'same';
    if (r.op === 'same' && only && !near) { skipped++; return; }
    flush(i);
    if (r.op === 'same') out.push(<div key={i} className="diff-row"><span className="diff-kind">{r.a.kind}</span><span>{r.a.text}</span></div>);
    else if (r.op === 'add') out.push(<div key={i} className="diff-row is-add"><span className="diff-kind">+ {r.b.kind}</span><span>{r.b.text || '—'}</span></div>);
    else if (r.op === 'del') out.push(<div key={i} className="diff-row is-del"><span className="diff-kind">− {r.a.kind}</span><span>{r.a.text || '—'}</span></div>);
    else out.push(
      <div key={i} className="diff-row is-mod"><span className="diff-kind">~ {r.b.kind}</span>
        <span>{r.words.map((w, k) => (w.op === 'same' ? <span key={k}>{w.t}</span> : <span key={k} className={w.op === 'add' ? 'w-add' : 'w-del'}>{w.t}</span>))}</span>
      </div>);
  });
  flush(rows.length);
  return <>{out}</>;
}
